import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";

const PROJECTS = [
    {
        id: "espacecostume",
        title: "Espace Costume",
        sector: "Boutique · Costumes",
        year: "2024",
        url: "https://www.espacecostume.com",
        image: "/portfolio/espacecostume.jpg",
        description:
            "Une vitrine e-commerce élégante pour une boutique de costumes locale.",
    },
    {
        id: "lepetitcarnet",
        title: "Le Petit Carnet",
        sector: "Marque artisanale",
        year: "2024",
        url: "https://www.lepetitcarnet.ca",
        image: "/portfolio/lepetitcarnet.jpg",
        description:
            "Un site vitrine élégant qui présente une application québécoise, avec lien de téléchargement pour l'application.",
    },
    {
        id: "groupesolido",
        title: "Groupe Solido",
        sector: "Construction",
        year: "2025",
        url: "https://www.groupesolido.com",
        image: "/portfolio/groupesolido.jpg",
        description:
            "Une plateforme corporative robuste pour un groupe en construction.",
    },
    {
        id: "flairimmobilier",
        title: "Flair Immobilier",
        sector: "Immobilier",
        year: "2025",
        url: "https://www.flairimmobilier.com/accueil",
        image: "/portfolio/flairimmobilier.jpg",
        description:
            "Une expérience web haut de gamme pour une agence immobilière en croissance.",
    },
    {
        id: "maderostudios",
        title: "Madero Studios",
        sector: "Studio créatif",
        year: "2025",
        url: "https://www.maderostudios.com",
        image: "/portfolio/maderostudios.jpg",
        description:
            "Un site portfolio audacieux pour un studio créatif partenaire.",
    },
];

export default function Portfolio() {
    return (
        <section
            id="portfolio"
            data-testid="portfolio-section"
            className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-scd"
        >
            <div className="grid md:grid-cols-12 gap-8 mb-14 md:mb-20">
                <div className="md:col-span-4">
                    <span className="label-eyebrow text-yellow">— Portfolio</span>
                </div>
                <div className="md:col-span-8">
                    <h2 className="section-heading text-cream" data-testid="portfolio-heading">
                        Récents
                        <br />
                        <span className="text-yellow italic">projets livrés.</span>
                    </h2>
                    <p className="mt-6 max-w-2xl text-cream/75 text-lg">
                        Chaque marque mérite une présence numérique à la hauteur de
                        son ambition. Voici quelques-unes de nos réalisations
                        récentes.
                    </p>
                </div>
            </div>

            <div className="space-y-6 md:space-y-10">
                {PROJECTS.map((p, i) => (
                    <ProjectRow key={p.id} project={p} index={i} />
                ))}
            </div>
        </section>
    );
}

function ProjectRow({ project, index }) {
    return (
        <motion.a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ y: 60, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`project-card-${project.id}`}
            className="group block border border-scd rounded-sm overflow-hidden bg-elev lift"
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
                <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                    <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                    />
                    {/* Yellow reveal mask sliding off on enter */}
                    <motion.div
                        aria-hidden
                        initial={{ scaleX: 1 }}
                        whileInView={{ scaleX: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{
                            duration: 0.95,
                            delay: 0.15 + index * 0.06,
                            ease: [0.76, 0, 0.24, 1],
                        }}
                        className="absolute inset-0 bg-yellow origin-right"
                    />
                    <div
                        aria-hidden
                        className="absolute inset-0 bg-gradient-to-t from-[#faf7f1]/30 via-transparent to-transparent"
                    />
                </div>
                <div className="lg:col-span-5 p-8 md:p-10 flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                        <span className="label-eyebrow text-cream/60">
                            {String(index + 1).padStart(2, "0")} / {project.year}
                        </span>
                        <span className="label-eyebrow text-yellow">{project.sector}</span>
                    </div>
                    <div className="mt-8">
                        <h3 className="font-display text-4xl md:text-5xl text-cream leading-none group-hover:text-yellow transition-colors">
                            {project.title}
                        </h3>
                        <p className="mt-4 text-cream/75 max-w-md">
                            {project.description}
                        </p>
                    </div>
                    <div className="mt-10 flex items-center gap-3 text-cream group-hover:text-yellow transition-colors">
                        <span className="label-eyebrow">Voir le site</span>
                        <ArrowUpRight
                            size={18}
                            weight="bold"
                            className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                        />
                    </div>
                </div>
            </div>
        </motion.a>
    );
}
